import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { usePromiseTracker } from 'react-promise-tracker';
import { getScenarioSplits, getStatusIdForUpdate } from './helper';
import { selectPlacementMarginPayPackageStatusModalDetails } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.selector';
import {
  selectPlacementMarginBookingPeriod,
  selectSelectedPlacementMarginScenario,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { usePayPackageStatusTransitionStyles } from './PayPackageStatusTransitionModalStyles.styles';
import { placementMarginPayPackageStatusActions } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.redux';
import {
  IUpdatePlacementMarginPayPackageStatusPayload,
  PlacementMarginPayPackageStatus,
  PlacementMarginStatusTransitionModalType,
} from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { formatDateToStartOfDay } from 'app/helpers/dateHelper';
import { useFormContext } from 'react-hook-form';
import { selectPlacementMarginTimeOffGridError } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { PostMarginToolResult as IPostMarginToolResult } from './models/AddScenarioModal';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';

const PayPackageStatusUpdate = () => {
  const { openModal, type } = useSelector(selectPlacementMarginPayPackageStatusModalDetails);
  const selectedScenario = useSelector(selectSelectedPlacementMarginScenario);
  const selectedBookingPeriod = useSelector(selectPlacementMarginBookingPeriod);
  const { userInfo } = useSelector(selectUser);
  const editTimeOffGridError = useSelector(selectPlacementMarginTimeOffGridError);

  const { promiseInProgress: isSavingScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.saveScenario,
    delay: 0,
  });

  const { promiseInProgress: isUpdateScenarioStatus } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.updateScenarioStatus,
    delay: 0,
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = usePayPackageStatusTransitionStyles({ type: type });
  const { promiseInProgress: updatePayPackageStatusTracker } = usePromiseTracker({
    area: 'get-scenario-navigation-tree-data',
    delay: 0,
  });

  const {
    watch,
    formState: { isDirty },
  } = useFormContext();

  const isPendingAndDirty =
    selectedScenario?.scenarioStatusId === PlacementMarginPayPackageStatus.Pending && isDirty && !editTimeOffGridError;

  const handleCancelAction = () => {
    dispatch(
      placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
        openModal: false,
        type: PlacementMarginStatusTransitionModalType.None,
      }),
    );
  };

  const handleSaveAction = (sendEmail: boolean) => {
    if (isPendingAndDirty && selectedScenario?.scenarioStatusId === PlacementMarginPayPackageStatus.Pending) {
      const formData = watch();
      new Promise((resolve, reject) => {
        dispatch(
          placementMarginScenarioActions.postPlacementMarginToolDetails({
            formData,
            loadDetails: false,
            isApprovalFlow: true,
            resolve,
            reject,
          }),
        );
      }).then((result: IPostMarginToolResult) => {
        const statusUpdatePayload: IUpdatePlacementMarginPayPackageStatusPayload = {
          bookingPeriodId: selectedScenario?.bookingPeriodId,
          placementId: selectedScenario?.placementId,
          statusId: getStatusIdForUpdate(type),
          payPackageId: selectedScenario?.scenarioId,
          bookingPeriodStartDate: formatDateToStartOfDay(selectedBookingPeriod?.bookingPeriodStartDate),
          bookingPeriodEndDate: formatDateToStartOfDay(selectedBookingPeriod?.bookingPeriodEndDate),
          scenarioSplitItemList: getScenarioSplits(selectedScenario),
          userId: userInfo?.employeeId,
          timestamp: result.data.payload,
        };

        const updatedScenarioPayload = selectedScenario?.notes
          ? { ...statusUpdatePayload, notes: selectedScenario?.notes }
          : { ...statusUpdatePayload };

        dispatch(
          placementMarginPayPackageStatusActions.updatePlacementMarginPayPackageStatus({
            ...updatedScenarioPayload,
            openEmailModal: sendEmail,
          }),
        );
      });
    } else {
      const statusUpdatePayload: IUpdatePlacementMarginPayPackageStatusPayload = {
        bookingPeriodId: selectedScenario?.bookingPeriodId,
        placementId: selectedScenario?.placementId,
        statusId: getStatusIdForUpdate(type),
        payPackageId: selectedScenario?.scenarioId,
        bookingPeriodStartDate: formatDateToStartOfDay(selectedBookingPeriod?.bookingPeriodStartDate),
        bookingPeriodEndDate: formatDateToStartOfDay(selectedBookingPeriod?.bookingPeriodEndDate),
        scenarioSplitItemList: getScenarioSplits(selectedScenario),
        userId: userInfo?.employeeId,
        notes: selectedScenario?.notes,
        timestamp: selectedScenario?.timestamp,
      };

      dispatch(
        placementMarginPayPackageStatusActions.updatePlacementMarginPayPackageStatus({
          ...statusUpdatePayload,
          openEmailModal: sendEmail,
        }),
      );
    }
  };

  const getModalTitle = () => {
    switch (type) {
      case PlacementMarginStatusTransitionModalType.RequestApproval:
        return t('marginTool.payPackageStatus.modalTitle.requestApproval');
      case PlacementMarginStatusTransitionModalType.Approve:
        return t('marginTool.payPackageStatus.modalTitle.approve');
      case PlacementMarginStatusTransitionModalType.Verify:
        return t('marginTool.payPackageStatus.modalTitle.verify');
      case PlacementMarginStatusTransitionModalType.Reset:
        return t('marginTool.payPackageStatus.modalTitle.reset');
      default:
        return '';
    }
  };

  const getModalDescription = () => {
    switch (type) {
      case PlacementMarginStatusTransitionModalType.RequestApproval:
        return t('marginTool.payPackageStatus.modalContent.requestApproval');
      case PlacementMarginStatusTransitionModalType.Approve:
        return t('marginTool.payPackageStatus.modalContent.approve');
      case PlacementMarginStatusTransitionModalType.Verify:
        return t('marginTool.payPackageStatus.modalContent.verify');
      case PlacementMarginStatusTransitionModalType.Reset:
        return t('marginTool.payPackageStatus.modalContent.reset');
      default:
        return '';
    }
  };

  const isLoading = isSavingScenario || isUpdateScenarioStatus || updatePayPackageStatusTracker;

  return (
    <>
      <GenericDialog
        open={openModal}
        maxWidth="sm"
        className={classes.dialog}
        style={{}}
        dialogTitleProps={{ text: getModalTitle() }}
        dialogContentProps={{
          classes: { root: classes.modalDescription },
        }}
        dialogActions={[
          {
            id: 'cancelButtonId',
            text: t('marginTool.payPackageStatus.modalButtons.cancel'),
            onClick: handleCancelAction,
            variant: 'text',
            className: classes.cancelButton,
            disabled: isLoading,
          },
          {
            id: 'saveAndCloseId',
            text: t('marginTool.payPackageStatus.modalButtons.saveClose'),
            onClick: () => {
              handleSaveAction(false);
            },
            variant: 'contained',
            color: 'tertiary',
            hidden:
              type === PlacementMarginStatusTransitionModalType.Verify ||
              type === PlacementMarginStatusTransitionModalType.Reset,
            className: classes.containedButton,
            disabled: isLoading,
          },
          {
            id: 'saveAndEmailId',
            text:
              type === PlacementMarginStatusTransitionModalType.Verify
                ? t('marginTool.payPackageStatus.modalButtons.verify')
                : type === PlacementMarginStatusTransitionModalType.Reset
                ? t('marginTool.payPackageStatus.modalButtons.reset')
                : t('marginTool.payPackageStatus.modalButtons.saveEmail'),
            onClick: () => {
              handleSaveAction(
                type === PlacementMarginStatusTransitionModalType.Verify ||
                  type === PlacementMarginStatusTransitionModalType.Reset
                  ? false
                  : true,
              );
            },
            variant: 'contained',
            className: classes.containedButton,
            disabled: isLoading,
          },
        ]}
      >
        {getModalDescription()}
      </GenericDialog>
    </>
  );
};

export default PayPackageStatusUpdate;
